import {Box, HStack, Show, Icon, Text, type ThemeIconKey} from 'platform/foundation';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';
import styled from 'styled-components';

import type {ReactNode} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {
  AdvertisementPreviewPlatform,
  GetAdvertisementPreviewApiResponse,
} from '@dms/api/sales';
import type {GetVehicleApiResponse} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {parseDate, type Nullish, type RequiredTestIdProps} from 'shared/web';

import {getAdvertisementPreviewSharedValues} from '../../utils/getAdvertisementPreviewSharedValues';
import {
  AdvertisementPreviewSharedValue,
  AdvertisementPreviewValue,
} from './AdvertisementPreviewValue';
import {getPreviewContainerBreakpoint} from './getPreviewContainerBreakpoint';
import {PREVIEW_CONTAINER_BREAKPOINTS} from './PREVIEW_CONTAINER_BREAKPOINTS';

export function AdvertisementPreviewKeyParameters(
  props: RequiredTestIdProps & {
    data: GetAdvertisementPreviewApiResponse;
    selectedPlatform?: AdvertisementPreviewPlatform;
    fuelLabel: string | Nullish;
    transmissionLabel: string | Nullish;
    specificationsVehicle?: GetVehicleApiResponse;
    isVehicleCatalogueLoading: boolean;
  }
) {
  const formatNumber = useFormatNumber();
  const formatDateTime = useDateTimeFormatter();
  const firstRegistration = isNotNilOrEmpty(props.data.vehicle.firstRegistrationOn)
    ? parseDate(props.data.vehicle.firstRegistrationOn)
    : null;
  const selectedMileage = isNotNil(props.selectedPlatform)
    ? {value: props.selectedPlatform.mileage, isShared: true}
    : getAdvertisementPreviewSharedValues(props.data.platforms).mileage;
  return (
    <Box
      flexGrow={0}
      flexShrink={0}
      flexBasis="auto"
      data-testid={`${props['data-testid']}-key-parameters`}
    >
      <KeyParametersGrid data-testid={`${props['data-testid']}-key-parameters-grid`}>
        <KeyParameterCell label={i18n.t('entity.vehicle.labels.mileage')} icon="automotive/mileage">
          <AdvertisementPreviewSharedValue
            sharedValue={selectedMileage}
            formatValue={(mileage) => `${formatNumber(mileage)} ${i18n.t('general.metric.km')}`}
          />
        </KeyParameterCell>
        <KeyParameterCell
          label={i18n.t('entity.vehicle.labels.firstRegistration')}
          icon="automotive/calendar"
        >
          <AdvertisementPreviewValue
            value={isNil(firstRegistration) ? null : formatDateTime('dateShort', firstRegistration)}
          />
        </KeyParameterCell>
        <KeyParameterCell label={i18n.t('entity.vehicle.labels.fuel')} icon="automotive/fuel_type">
          <AdvertisementPreviewValue
            value={props.fuelLabel}
            isNeutral={props.isVehicleCatalogueLoading}
          />
        </KeyParameterCell>
        <KeyParameterCell label={i18n.t('entity.vehicle.labels.power')} icon="automotive/engine">
          <AdvertisementPreviewValue
            value={isNil(props.data.vehicle.power) ? null : `${props.data.vehicle.power} kW`}
          />
        </KeyParameterCell>
        <KeyParameterCell
          label={i18n.t('entity.vehicle.labels.transmission')}
          icon="automotive/transmission"
        >
          <AdvertisementPreviewValue
            value={props.transmissionLabel}
            isNeutral={props.isVehicleCatalogueLoading}
          />
        </KeyParameterCell>
        <Show when={typeof props.specificationsVehicle?.ownerCount === 'number'}>
          <KeyParameterCell
            label={i18n.t('entity.vehicle.labels.previousOwners')}
            icon="social/people_stroke"
            isNotFilled
          >
            <AdvertisementPreviewValue
              value={props.specificationsVehicle?.ownerCount?.toString()}
            />
          </KeyParameterCell>
        </Show>
      </KeyParametersGrid>
    </Box>
  );
}
function KeyParameterCell(props: {
  label: string;
  icon: ThemeIconKey;
  isNotFilled?: boolean;
  children: ReactNode;
}) {
  return (
    <Box padding={1} minWidth={0}>
      <HStack align="flex-start" spacing={2} minWidth={0}>
        <KeyParameterIconSlot aria-hidden="true">
          <Icon
            value={props.icon}
            size={6}
            color="general.accent"
            isNotFilled={props.isNotFilled}
          />
        </KeyParameterIconSlot>
        <KeyParameterText>
          <Text size="small" color="secondary">
            {props.label}
          </Text>
          <KeyParameterValue>{props.children}</KeyParameterValue>
        </KeyParameterText>
      </HStack>
    </Box>
  );
}

export const KeyParametersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${({theme}) => theme.getSize(3)};
  padding: ${({theme}) => theme.getSize(3)};
  border: 1px solid ${({theme}) => theme.colors.general.separator};
  border-radius: ${({theme}) => theme.radii.medium};
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.wide
  )}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.phone
  )}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
export const KeyParameterText = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: ${({theme}) => theme.getSize(1)};
  overflow-wrap: anywhere;
`;
const KeyParameterValue = styled.div`
  max-width: 100%;
  overflow-wrap: anywhere;
`;

// eslint-disable-next-line eag/no-css-property -- Automotive theme SVGs hardcode stroke; recolour to accent
export const KeyParameterIconSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 ${({theme}) => theme.getSize(12)};
  height: ${({theme}) => theme.getSize(12)};
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.compact
  )}) {
    flex-basis: ${({theme}) => theme.getSize(6)};
    height: ${({theme}) => theme.getSize(8)};
  }

  svg [stroke]:not([stroke='none']) {
    stroke: currentColor;
  }
`;
